import { useState } from 'react';
import { History } from 'lucide-react';
import FeatureLocked from '../../../../../../components/gating/FeatureLocked';
import { format24HourDateTime, postTripData } from '../../../../utils/postTripHelpers';

const HistoryCard = ({ item }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h4 className="font-bold text-slate-800 text-base">{item.title}</h4>
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-teal-50 text-[#067978] rounded-md uppercase">
                            {item.milestone === 'custom_bulk' ? 'Bulk Custom' : item.milestone === 'welcome_back' ? 'Welcome Back' : 'Feedback Nudge'}
                        </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{item.message}</p>
                </div>
                <button 
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="text-xs font-bold text-[#067978] hover:text-[#056362] bg-[#e6f3ec] hover:bg-[#cfe6da] px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer"
                >
                    {expanded ? 'Hide Details' : `Recipients (${item.recipients?.length || 1})`}
                </button>
            </div>
            
            {expanded && item.recipients && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                    <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Recipients List</h5>
                    <div className="max-h-40 overflow-y-auto space-y-1.5 pr-2">
                        {item.recipients.map((rec, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-slate-50 px-3 py-2 rounded-xl text-xs">
                                <span className="font-semibold text-slate-700">Booking: <strong className="uppercase text-slate-900">{rec.booking_id}</strong></span>
                                <span className="text-slate-500">Guest: <strong className="text-slate-700">{rec.guest_name || 'All'}</strong></span>
                                <span className={`px-2 py-0.5 rounded-md font-bold text-[9px] uppercase ${
                                    rec.readBy && rec.readBy.length > 0 
                                        ? 'bg-green-50 text-green-600' 
                                        : rec.hasPushToken 
                                        ? 'bg-teal-50 text-teal-700' 
                                        : 'bg-slate-100 text-slate-500'
                                }`}>
                                    {rec.readBy && rec.readBy.length > 0 ? 'Read' : rec.hasPushToken ? 'Delivered (Push)' : 'In-App Only'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
                <span>Total Sent: <strong className="text-slate-600">{item.recipients?.length || 1}</strong></span>
                <span>•</span>
                <span>Date: <strong className="text-slate-600">{format24HourDateTime(item.createdAt)}</strong></span>
            </div>
        </div>
    );
};

const SentHistory = ({
    history,
    loading,
    isNotificationsLocked
}) => {
    const sampleGhostHistory = postTripData.sampleGhostHistory || [];

    return (
        <div className="relative min-h-[480px] pb-4">
            {loading ? (
                <div className="py-12 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#067978]"></div>
                </div>
            ) : isNotificationsLocked ? (
                /* Sample Ghost History in Background */
                <div className="space-y-3 select-none pointer-events-none opacity-75">
                    {sampleGhostHistory.map((item) => (
                        <HistoryCard key={item._id} item={item} />
                    ))}
                </div>
            ) : history.length === 0 ? (
                <div className="relative w-full overflow-hidden pb-4">
                    {/* 1. Sample Ghost History in Background */}
                    <div className="space-y-3 select-none opacity-40 filter blur-[0.3px]">
                        {sampleGhostHistory.map((item) => (
                            <HistoryCard key={item._id} item={item} />
                        ))}
                    </div>

                    {/* 2. Elevated Floating Empty State Card */}
                    <div className="relative z-20 -mt-48 sm:-mt-56 md:-mt-64 px-3 sm:px-6 flex justify-center items-center">
                        <div className="w-full max-w-md p-6 sm:p-8 text-center bg-white rounded-3xl border border-slate-200/90 shadow-sm relative backdrop-blur-md">
                            <div className="w-14 h-14 bg-teal-50 text-[#067978] rounded-2xl flex items-center justify-center mx-auto mb-4 border border-teal-100/80 shadow-2xs">
                                <History size={28} />
                            </div>
                            <h2 className="text-xl sm:text-2xl font-normal text-slate-900 tracking-tight">
                                No sent notification history
                            </h2>
                            <p className="text-xs sm:text-[13px] text-slate-500 leading-relaxed font-normal max-w-sm mx-auto mt-2">
                                Past notifications sent will appear here along with delivery details.
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-3 animate-in fade-in duration-200">
                    {history.map((item) => (
                        <HistoryCard key={item._id} item={item} />
                    ))}
                </div>
            )}

            {isNotificationsLocked && (
                <FeatureLocked featureKey="notifications" overlay={true} />
            )}
        </div>
    );
};

export default SentHistory;
